import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";

import NoRecordsFound from "components/Records/NoRecordsFound";
import {
  RectangularTextButton,
} from "components/Base/Buttons";

export interface DatabasePersonListItem {
  id: number;
  primary: string;
  secondary?: string;
}

interface AddPeopleFromDatabasePopupProps<
  T extends DatabasePersonListItem,
> {
  open: boolean;

  /**
   * Complete collection of people that can potentially
   * be selected from the database.
   */
  people: T[];

  /**
   * People with these IDs will not appear in the dialog.
   * This can be used to prevent already-added people from
   * being selected again.
   */
  excludedPersonIds?: number[];

  loading?: boolean;

  title?: string;
  searchLabel?: string;
  emptyItemName?: string;

  onClose: () => void;

  onAdd: (
    selectedPeople: T[]
  ) => Promise<void> | void;
}

const AddPeopleFromDatabasePopup = <
  T extends DatabasePersonListItem,
>({
  open,
  people,
  excludedPersonIds = [],
  loading = false,
  title = "Add Person from Database",
  searchLabel = "Search people",
  emptyItemName = "available people",
  onClose,
  onAdd,
}: AddPeopleFromDatabasePopupProps<T>) => {
  const [search, setSearch] =
    useState("");

  const [
    selectedPersonIds,
    setSelectedPersonIds,
  ] = useState<number[]>([]);

  const excludedIds = useMemo(
    () =>
      new Set(
        excludedPersonIds.map((id) =>
          Number(id)
        )
      ),
    [excludedPersonIds]
  );

  const availablePeople = useMemo(() => {
    const normalisedSearch = search
      .trim()
      .toLowerCase();

    return people
      .filter(
        (person) =>
          !excludedIds.has(
            Number(person.id)
          )
      )
      .filter((person) => {
        if (!normalisedSearch) {
          return true;
        }

        const primary =
          person.primary
            ?.trim()
            .toLowerCase() ?? "";

        const secondary =
          person.secondary
            ?.trim()
            .toLowerCase() ?? "";

        return (
          primary.includes(
            normalisedSearch
          ) ||
          secondary.includes(
            normalisedSearch
          )
        );
      })
      .sort((a, b) =>
        a.primary.localeCompare(
          b.primary
        )
      );
  }, [people, excludedIds, search]);

  /*
   * Reset the popup whenever it is opened.
   * This gives each use of the dialog a clean
   * search and selection state.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    setSearch("");
    setSelectedPersonIds([]);
  }, [open]);

  /*
   * Remove selected IDs if their people are
   * subsequently excluded or removed from
   * the supplied database list.
   */
  useEffect(() => {
    const availableIds = new Set(
      availablePeople.map((person) =>
        Number(person.id)
      )
    );

    setSelectedPersonIds(
      (previousIds) =>
        previousIds.filter((id) =>
          availableIds.has(id)
        )
    );
  }, [availablePeople]);

  const closePopup = () => {
    if (loading) {
      return;
    }

    setSearch("");
    setSelectedPersonIds([]);
    onClose();
  };

  const togglePerson = (
    personId: number
  ) => {
    const numericPersonId =
      Number(personId);

    setSelectedPersonIds(
      (previousIds) =>
        previousIds.includes(
          numericPersonId
        )
          ? previousIds.filter(
              (id) =>
                id !== numericPersonId
            )
          : [
              ...previousIds,
              numericPersonId,
            ]
    );
  };

  const handleAdd = async () => {
    const selectedPeople =
      people.filter((person) =>
        selectedPersonIds.includes(
          Number(person.id)
        )
      );

    if (selectedPeople.length === 0) {
      return;
    }

    try {
      await onAdd(selectedPeople);

      setSearch("");
      setSelectedPersonIds([]);
      onClose();
    } catch (error) {
      console.error(
        "Could not add database people:",
        error
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={
        loading
          ? undefined
          : closePopup
      }
      fullWidth
      maxWidth="sm"
      fullScreen={false}
      PaperProps={{
        sx: {
          width: {
            xs: "calc(100% - 24px)",
            sm: "100%",
          },

          maxHeight: {
            xs: "calc(100% - 24px)",
            sm: "calc(100% - 64px)",
          },

          m: {
            xs: 1.5,
            sm: 4,
          },

          borderRadius: {
            xs: 2,
            sm: 2.5,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,

          fontSize: {
            xs: "1.1rem",
            sm: "1.25rem",
          },
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={searchLabel}
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          disabled={loading}
          sx={{
            mt: 1,
            mb: 2,
          }}
        />

        {availablePeople.length ===
        0 ? (
          <NoRecordsFound
            itemName={emptyItemName}
            minHeight={100}
            maxHeight={160}
          />
        ) : (
          <List
            disablePadding
            sx={{
              maxHeight: {
                xs: "55vh",
                sm: 420,
              },

              overflowY: "auto",
              overscrollBehavior:
                "contain",

              pr: {
                xs: 0,
                sm: 0.5,
              },
            }}
          >
            {availablePeople.map(
              (person) => {
                const personId =
                  Number(person.id);

                const checked =
                  selectedPersonIds.includes(
                    personId
                  );

                return (
                  <ListItemButton
                    key={personId}
                    selected={checked}
                    disabled={loading}
                    onClick={() =>
                      togglePerson(
                        personId
                      )
                    }
                    sx={{
                      minHeight: 54,

                      px: {
                        xs: 0.75,
                        sm: 1,
                      },

                      borderRadius: 1,
                      mb: 0.5,

                      "&.Mui-selected": {
                        backgroundColor:
                          "action.selected",
                      },

                      "&.Mui-selected:hover":
                        {
                          backgroundColor:
                            "action.selected",
                        },
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      disabled={loading}
                      inputProps={{
                        "aria-label": `Select ${person.primary}`,
                      }}
                      sx={{
                        mr: {
                          xs: 0.5,
                          sm: 1,
                        },
                      }}
                    />

                    <ListItemText
                      primary={
                        person.primary
                      }
                      secondary={
                        person.secondary
                      }
                      sx={{
                        minWidth: 0,
                      }}
                      slotProps={{
                        primary: {
                          sx: {
                            fontWeight:
                              checked
                                ? 600
                                : 500,

                            overflow:
                              "hidden",

                            textOverflow:
                              "ellipsis",

                            whiteSpace:
                              "nowrap",
                          },
                        },

                        secondary: {
                          sx: {
                            overflow:
                              "hidden",

                            textOverflow:
                              "ellipsis",

                            whiteSpace:
                              "nowrap",
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                );
              }
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },

          pb: {
            xs: 2,
            sm: 2.5,
          },

          gap: 1,

          flexDirection: {
            xs: "column-reverse",
            sm: "row",
          },

          "& > :not(style) ~ :not(style)":
            {
              ml: {
                xs: 0,
                sm: 1,
              },
            },
        }}
      >
        <RectangularTextButton
          colour="highlighted"
          onClick={closePopup}
          disabled={loading}
          sx={{
            width: {
              xs: "100%",
              sm: "auto",
            },
          }}
        >
          Cancel
        </RectangularTextButton>

        <RectangularTextButton
          colour="highlighted"
          onClick={handleAdd}
          disabled={
            selectedPersonIds.length ===
              0 ||
            loading
          }
          startIcon={
            loading ? (
              <CircularProgress
                size={18}
                color="inherit"
              />
            ) : undefined
          }
          sx={{
            width: {
              xs: "100%",
              sm: "auto",
            },
          }}
        >
          {loading
            ? "Adding..."
            : selectedPersonIds.length >
                0
              ? `Add (${selectedPersonIds.length})`
              : "Add"}
        </RectangularTextButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddPeopleFromDatabasePopup;